import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Menu, 
  X, 
  LogOut, 
  User,
  ChevronDown,
  Megaphone,
  Send,
  Workflow,
  ChevronLeft,
  ChevronRight,
  Search,
  Shield,
  MessageSquare,
  Inbox,
  GitBranch
} from 'lucide-react';
import toast from 'react-hot-toast';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, isAdmin, adminUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Customer navigation
  const customerNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Campaign Management', href: '/campaigns', icon: Megaphone },
    { name: 'Template Manager', href: '/template-manager', icon: FileText },
    { name: 'Bot Management', href: '/bots', icon: Workflow },
    { name: 'Test Messages', href: '/test-messages', icon: Send },
    { name: 'Inquiries', href: '/inquiries', icon: Inbox },
    { name: 'WhatsApp Flows', href: '/whatsapp-flows', icon: GitBranch },
    { name: 'Contacts & Messages', href: '/contacts', icon: MessageSquare }
  ];

  // Admin navigation
  const adminNavigation = [
    { name: 'Admin Dashboard', href: '/admin/dashboard', icon: Shield },
    { name: 'Customer Search', href: '/admin/customers', icon: Search },
    { name: 'Template Management', href: '/template-manager', icon: FileText },
    { name: 'Bot Management', href: '/bots', icon: Workflow }
  ];

  // Use appropriate navigation based on user type
  const navigation = isAdmin ? adminNavigation : customerNavigation;

  const handleLogout = () => {
    logout(); // Clears state immediately
    navigate('/login'); // Navigate immediately
    toast.success('Logged out successfully'); // Show toast (non-blocking)
  };

  const isActive = (href) => location.pathname === href;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-white" />
              </div>
              <div className="ml-3 text-left">
                <p className="text-sm font-medium text-gray-900">
                  {isAdmin ? adminUser?.email : user?.customerName}
                </p>
                <p className="text-xs text-gray-500">
                  {isAdmin ? 'Admin' : user?.email}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <nav className="flex-1 space-y-0 px-3 py-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                  isActive(item.href)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <item.icon className="h-6 w-6 mr-3" />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ${
        sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
      }`}>
        <div className="flex flex-col flex-grow bg-white">
          <div className={`flex h-16 items-center ${sidebarCollapsed ? 'justify-center px-2' : 'px-6'}`}>
            <div className="relative flex items-center">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center hover:bg-gray-100 rounded-lg p-1 transition-colors"
                title={sidebarCollapsed ? 'User Menu' : ''}
              >
                <div className="h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-white" />
                </div>
                {!sidebarCollapsed && (
                  <div className="ml-3 text-left">
                    <p className="text-sm font-medium text-gray-900">
                      {isAdmin ? adminUser?.email : user?.customerName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {isAdmin ? 'Admin' : user?.email}
                    </p>
                  </div>
                )}
              </button>

              {/* User menu dropdown */}
              {userMenuOpen && (
                <div className={`absolute ${sidebarCollapsed ? 'left-full ml-2' : 'right-0'} mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50`}>
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {isAdmin ? adminUser?.email : user?.customerName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {isAdmin ? 'Admin' : user?.email}
                    </p>
                  </div>
                  {!isAdmin && (
                    <Link
                      to="/profile"
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      Profile
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Collapse/Expand button */}
          <div className="px-3 py-2">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-full flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </button>
          </div>

          
          <nav className="flex-1 space-y-0 px-3 py-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group relative flex items-center px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                  isActive(item.href)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                title={sidebarCollapsed ? item.name : ''}
              >
                <item.icon className={`h-6 w-6 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                {!sidebarCollapsed && (
                  <span className="transition-opacity duration-300">
                    {item.name}
                  </span>
                )}
                
                {/* Tooltip for collapsed state */}
                {sidebarCollapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                    {item.name}
                  </div>
                )}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        {/* Mobile menu button - floating */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg text-gray-400 hover:text-gray-600 border border-gray-200"
        >
          <Menu className="h-6 w-6" />
        </button>

        {/* Mobile user menu - floating */}
        <div className="lg:hidden fixed top-4 right-4 z-50">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center space-x-3 text-sm rounded-lg p-2 bg-white shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <div className="h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center">
              <User className="h-4 w-4 text-white" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">
                {isAdmin ? adminUser?.email : user?.customerName}
              </p>
              <p className="text-xs text-gray-500">
                {isAdmin ? 'Admin' : user?.email}
              </p>
            </div>
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">
                  {isAdmin ? adminUser?.email : user?.customerName}
                </p>
                <p className="text-xs text-gray-500">
                  {isAdmin ? 'Admin' : user?.email}
                </p>
              </div>
              {!isAdmin && (
                <Link
                  to="/profile"
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  onClick={() => setUserMenuOpen(false)}
                >
                  <User className="h-4 w-4 mr-2" />
                  Profile
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </button>
            </div>
          )}
        </div>

        {/* Page content */}
        <main className="h-full">
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default Layout;
